import { useTenant } from "../components/TenantContext";

/**
 * A hook to retrieve FAQ attributes for a specific entity by its name.
 * This hook is tolerant: it first tries to find the FAQ object directly
 * in the fetched estimate objects (case-insensitive name match). If that
 * fails it falls back to the existing context helpers that resolve by
 * entity id.
 *
 * @param entityName The name of the entity for which to find FAQ attributes.
 * @returns An object containing the FAQ attributes and a loading state.
 */
export const useFAQEntityByName = (entityName: string) => {
  const {
    getEntityIdByName,
    getFAQAttributesByEntityId,
    loading,
    derivedAttributesData,
  } = useTenant();

  // While the context is loading, we cannot resolve the entity, so we return a loading state.
  if (loading) {
    return { faqAttributes: [], isLoading: true };
  }

  // Try direct lookup in the derivedAttributesData objects first (more reliable
  // for FAQ objects returned by the estimates API).
  const objects: any[] | undefined = derivedAttributesData?.objects;
  if (objects && objects.length > 0) {
    const targetName = entityName?.toLowerCase().trim();

    // Exact case-insensitive match
    let found = objects.find(
      (obj: any) =>
        typeof obj.name === "string" &&
        obj.name.toLowerCase().trim() === targetName,
    );

    // Fallback: name contains both parts (e.g. 'welcome' and 'faq') or a substring match
    if (!found) {
      const tokens = targetName.split(/\s{0,10}-\s{0,10}|\s+/).filter(Boolean);
      found = objects.find((obj: any) => {
        if (typeof obj.name !== "string") return false;
        const nameLower = obj.name.toLowerCase();
        return tokens.every((t) => nameLower.includes(t));
      });
    }

    if (found) {
      return { faqAttributes: found.attributes ?? [], isLoading: false };
    }
  }

  // Fallback: try resolving by entity id using existing helpers
  const entityId = getEntityIdByName(entityName);
  if (!entityId) {
    return { faqAttributes: [], isLoading: false };
  }

  const faqAttributes = getFAQAttributesByEntityId(entityId);
  return { faqAttributes, isLoading: false };
};
